import * as d3 from 'd3-selection'
import * as shape from 'd3-shape'
import * as colors from 'colors'

class Forecast {

  constructor ({serie, xScale, yScale, chart, previous, styles}) {
    this.serie = serie
    this.xScale = xScale
    this.yScale = yScale
    this.chart = chart
    this.previous = previous
    this.styles = styles

    // This forecast is called on area animations, too. We aren't
    // showing forecasts on area charts for now.
    this._data = this.serie.type === 'line' ? this.serie.forecast : null
  }

  /*
   * Builds a line generator to generate the main forecast line using
   * the incoming scales
   */
  _lineGenerator (xScale = this.xScale, yScale = this.yScale) {
    return shape.line()
                .x((d, i) => xScale(this.chart.xAccessor(d, i), i))
                .y(d => yScale(d.raw))
  }

  /*
   * Builds an area generator to build the forecast cone. y0, y1 can be
   * set to change how the area y values are set. By default they use
   * the raw value of the point, which creates a flat area drawing.
   */
  _areaGenerator (xScale = this.xScale, yScale = this.yScale, y0, y1) {
    if (!y0) {
      y0 = d => yScale(d.raw)
    }

    if (!y1) {
      y1 = d => yScale(d.raw)
    }

    return shape.area().x((d, i) => xScale(this.chart.xAccessor(d, i), i))
                       .y0(y0)
                       .y1(y1)
  }

  /*
   * Renders the forecast drawing.
   *
   *
   * existingXScale and enteringXScale can be used to override the x-scale
   * functions. This is useful when the range is being extended or chopped. See
   * sliding-animation.
   */
  render (context, existingXScale = this.xScale, enteringXScale = this.xScale) {
    let selection = context.selection ? context.selection() : context
      , forecast = selection.selectAll('.forecast')
                            .data(this._data ? [this._data] : [])
      , enter = forecast.enter()
                        .insert('g', ':first-child')
                          .attr('class', 'forecast')
      , exit = forecast.exit()
      , previousY = this.previous.yScale || this.yScale
      , area0 = this._areaGenerator(existingXScale, previousY)
      , serie = selection.datum()
    forecast = forecast.merge(enter)

    enter.append('path')
         .attr('class', 'forecast-cone')
         .attr('d', d => area0(d.points))
         .style('fill', d => colors.lookup(d.fill || serie.color))

    enter.append('path')
         .attr('class', 'forecast-line')
         .style('stroke-width', '0px')
         .style('stroke', d => colors.lookup(d.color || serie.color))
         .attr('d', d => this._lineGenerator(existingXScale, this.previous.yScale)(d.points))

    // Make sure to rebind the data for updates
    let cone = forecast.selectAll('path.forecast-cone').data(d => [d])
      , line = forecast.selectAll('path.forecast-line').data(d => [d])

    if (selection != context) {
      // Inherit the transition
      cone = cone.transition(context)
      line = line.transition(context)

      exit = exit.transition(context)
      exit.remove()
    }

    cone.attr('class', 'forecast-cone')
        .style('fill', d => colors.lookup(d.fill || serie.color))
        .attr('d', d => {
          let gen =  this._areaGenerator(this.xScale, this.yScale, d => this.yScale(d.range[0]), d => this.yScale(d.range[1]))

          return gen(d.points)
        })

    line.attr('class', 'forecast-line')
        .style('stroke', d => colors.lookup(d.color || serie.color))
        .style('stroke-width', this.styles.lineStroke)
        .attr('d', d => this._lineGenerator()(d.points))

    exit.selectAll('path.forecast-cone')
        .attr('d', d => this._areaGenerator(enteringXScale, this.yScale)(d.points))

    exit.selectAll('path.forecast-line')
        .attr('d', d => this._lineGenerator(enteringXScale)(d.points))
        .style('stroke-width', '0px')
  }

}

export default Forecast
